import { isNullable } from '@ipfabric/utils';
import { List, Record, fromJS } from 'immutable';
import { pipe } from 'ramda';
import {
  JOB_ADDED,
  JOB_CANCEL,
  JOB_CANCELLED,
  JOB_QUEUED,
  JOB_STATUSES,
  JOB_STOP,
  JOB_UPDATED,
  READ_ALL_JOBS,
  READ_JOB,
  SET_STOP_JOB,
} from './constants';

const InitialState = Record({
  jobs: new List(),
  cancelling: new List(),
  reading: new List(),
  stopping: new List(),
  stopJobId: null,
  stopJobAction: '',
});

const QUEUED_PRIORITY = {
  error: 1,
  done: 1,
  stopping: 2,
  running: 3,
  scheduled: 4,
};

const initialState = new InitialState();

const sortChange = (a, b) => {
  if (QUEUED_PRIORITY[a.get('status')] > QUEUED_PRIORITY[b.get('status')]) {
    return 1;
  }
  if (QUEUED_PRIORITY[a.get('status')] < QUEUED_PRIORITY[b.get('status')]) {
    return -1;
  }
  return (
    b.get('finishedAt') - a.get('finishedAt') ||
    a.get('startedAt') - b.get('startedAt') ||
    b.get('priority') - a.get('priority') ||
    a.get('scheduledAt') - b.get('scheduledAt')
  );
};

export default function jobManagementReducer(
  state = initialState,
  action = undefined,
) {
  switch (action.type) {
    case JOB_QUEUED:
      return state.set('jobs', fromJS(action.payload).sort(sortChange));
    case JOB_CANCEL: {
      return state.update('cancelling', (cancelling) =>
        cancelling.push(action.payload),
      );
    }
    case `${JOB_CANCEL}_SUCCESS`:
    case `${JOB_CANCEL}_ERROR`: {
      const foundIndex = state.get('cancelling').indexOf(action.payload);
      return state.update('cancelling', (cancelling) =>
        cancelling.delete(foundIndex),
      );
    }
    case JOB_STOP: {
      return state.update('stopping', (stopping) =>
        stopping.push(action.payload),
      );
    }
    case `${JOB_STOP}_SUCCESS`:
    case `${JOB_STOP}_ERROR`: {
      const foundIndex = state.get('stopping').indexOf(action.payload);
      return state.update('stopping', (stopping) =>
        stopping.delete(foundIndex),
      );
    }
    case JOB_CANCELLED: {
      const cancelledJobs = action.payload;
      return pipe(
        (s) =>
          s.update('jobs', (jobs) =>
            jobs.filter((job) => {
              const id = job.get('id');
              const foundIndex = cancelledJobs.findIndex(
                (cancelledJob) => cancelledJob.id === id,
              );
              return foundIndex === -1;
            }),
          ),
        (s) => {
          const cancelling = state.get('cancelling');
          const foundIndex = cancelledJobs.findIndex(
            (cancelledJob) => cancelling.indexOf(cancelledJob.id) > -1,
          );
          if (foundIndex > -1) {
            return s.update('cancelling', (data) => data.delete(foundIndex));
          }
          return s;
        },
        (s) => {
          const stopping = state.get('stopping');
          const foundIndex = cancelledJobs.findIndex(
            (cancelledJob) => stopping.indexOf(cancelledJob.id) > -1,
          );
          if (foundIndex > -1) {
            return s.update('stopping', (data) => data.delete(foundIndex));
          }
          return s;
        },
        (s) => s.sort(sortChange),
      )(state);
    }
    case JOB_UPDATED: {
      const updatedJobs = action.payload;
      return pipe(
        (s) => {
          const stopJobId = state.get('stopJobId');
          if (isNullable(stopJobId)) {
            return s;
          }
          const stopJobDone = updatedJobs.find(
            (job) => job.id === stopJobId && job.status === 'done',
          );
          if (stopJobDone) {
            return s.set('stopJobId', null);
          }
          return s;
        },
        (s) =>
          s.update('jobs', (jobs) =>
            jobs
              .map((job) => {
                const id = job.get('id');
                const foundJob = updatedJobs.find(
                  (updatedJob) => updatedJob.id === id,
                );
                if (foundJob) {
                  return job.merge(fromJS(foundJob));
                }
                return job;
              })
              .filter((job) => {
                return !job.get('read');
              })
              .sort(sortChange),
          ),
      )(state);
    }
    case JOB_ADDED:
      return state.update('jobs', (jobs) =>
        jobs.concat(fromJS(action.payload)).sort(sortChange),
      );
    case SET_STOP_JOB:
      return state
        .set('stopJobId', action.payload.id)
        .set('stopJobAction', action.payload.action);
    case READ_JOB:
      return state.update('reading', (reading) =>
        reading.push(action.payload.id),
      );
    case `${READ_JOB}_SUCCESS`:
    case `${READ_JOB}_ERROR`: {
      const foundIndex = state.get('reading').indexOf(action.payload.id);
      return state.update('reading', (reading) => reading.delete(foundIndex));
    }
    case READ_ALL_JOBS: {
      const jobsToRead = state
        .get('jobs')
        .filter(
          (job) =>
            (job.get('done') &&
              job.get('status') !== JOB_STATUSES.error &&
              action.payload.authId === job.get('userId')) ||
            job.get('status') === JOB_STATUSES.error,
        )
        .map((job) => job.get('id'));
      return state.set('reading', state.get('reading').merge(jobsToRead));
    }
    case `${READ_ALL_JOBS}_SUCCESS`:
    case `${READ_ALL_JOBS}_ERROR`:
      return state.set('reading', new List());
    default:
      return state;
  }
}
